import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import logo from '../../assets/images/logo.svg'; // Your SVG logo
import { Canvg } from 'canvg'; // Import Canvg

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Extend jsPDF interface
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface AppointmentData {
  name: string;
  email: string;
  phone: string;
  service: string;
  date: string;
  timeSlot: string;
  location: string;
  advisor: string;
  note: string;
  price: number;
  paymentStatus?: string;
}

const Success = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [appointmentData, setAppointmentData] =
    useState<AppointmentData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [logoPngBase64, setLogoPngBase64] = useState<string | null>(null); // State to store PNG base64 logo

  const serviceLabels: Record<string, string> = {
    corporate: 'Corporate Legal Solution & Compliance',
    resolution: 'Litigation & Dispute Resolution',
    Legal_Service: 'Land Revenue Litigation & Real Estate Legal Service',
    LPO_Service: 'High-Volume Legal Drafting & LPO Service',
  };

  // Effect to convert SVG logo to PNG base64 using canvg
  useEffect(() => {
    const convertSvgToPngBase64 = async () => {
      try {
        // Fetch the SVG content
        const response = await fetch(logo);
        const svgText = await response.text();

        // Create a temporary canvas element
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Render the SVG onto the canvas
        // Adjust these values as needed to control the quality and size in the PDF
        const desiredWidth = 400; // Example: Render SVG at a higher resolution for better PDF quality
        const desiredHeight = 100; // Adjust aspect ratio as needed based on your logo
        canvas.width = desiredWidth;
        canvas.height = desiredHeight;

        const v = Canvg.fromString(ctx!, svgText);
        await v.render();

        // Get the data URL for PNG
        const pngDataUrl = canvas.toDataURL('image/png');
        setLogoPngBase64(pngDataUrl);
      } catch (err) {
        console.error('Error converting SVG logo to PNG base64:', err);
      }
    };

    convertSvgToPngBase64();
  }, []); // Run once on component mount

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');
    const paymentSuccess = urlParams.get('success');

    console.log('URL Parameters:', {
      paymentSuccess,
      sessionId,
      fullUrl: window.location.href,
    });

    if (paymentSuccess === 'true' && sessionId) {
      console.log(
        'Payment success detected, fetching appointment for sessionId:',
        sessionId
      );
      fetch(`${API_BASE_URL}/sendappointment/session/${sessionId}`)
        .then((res) => {
          console.log(
            'Fetch response status:',
            res.status,
            'Response OK:',
            res.ok
          );
          if (!res.ok) {
            throw new Error(
              `Failed to fetch appointment: ${res.status} ${res.statusText}`
            );
          }
          return res.json();
        })
        .then((data: AppointmentData) => {
          console.log('Appointment data received:', data);
          if (data.paymentStatus === 'paid') {
            console.log('Payment confirmed, showing success alert and modal');
            Swal.fire({
              title: 'Payment Successful!',
              text: 'Your payment was successful and your appointment is confirmed. Please download your appointment details.',
              icon: 'success',
              confirmButtonText: 'OK',
              confirmButtonColor: '#db2217',
              timer: 5000,
              timerProgressBar: true,
            }).then(() => {
              setAppointmentData(data);
              setShowModal(true);
            });
          } else {
            console.log('Payment not confirmed, status:', data.paymentStatus);
            setError('Payment not confirmed. Please contact support.');
            Swal.fire({
              title: 'Payment Issue',
              text: 'Payment not confirmed. Please contact support.',
              icon: 'error',
              confirmButtonText: 'OK',
              confirmButtonColor: '#db2217',
            }).then(() => {
              navigate('/');
            });
          }
        })
        .catch((err) => {
          console.error('Error fetching appointment:', err);
          setError(
            'Failed to verify payment status. Please try again or contact support.'
          );
          Swal.fire({
            title: 'Error',
            text: 'Failed to verify payment status. Please try again or contact support.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#db2217',
          }).then(() => {
            navigate('/');
          });
        });
    } else if (paymentSuccess === 'false') {
      console.log('Payment failed or cancelled');
      setError('Payment was not completed. Please try again.');
      Swal.fire({
        title: 'Payment Cancelled',
        text: 'Your payment was cancelled or failed. Please try again.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#db2217',
      }).then(() => {
        navigate('/');
      });
    } else {
      console.log('No valid payment status or session ID in URL');
      setError(
        'Invalid or missing payment status. Please try again or contact support.'
      );
      Swal.fire({
        title: 'Error',
        text: 'Invalid or missing payment status. Please try again or contact support.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#db2217',
      }).then(() => {
        navigate('/');
      });
    }
  }, [location, navigate]);

  const handleDownloadPdf = () => {
    // Ensure both appointment data and the PNG logo are ready
    if (!appointmentData || !logoPngBase64) {
      console.error(
        'Appointment data or logo not available for PDF generation.'
      );
      Swal.fire({
        title: 'PDF Generation Error',
        text: 'Required data for PDF download is not ready. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#db2217',
      });
      return;
    }

    const doc = new jsPDF();

    // Set document properties
    doc.setProperties({
      title: 'Appointment Confirmation',
      subject: `Appointment for ${appointmentData.name}`,
      author: 'Shahlpo Legal Services',
      keywords: 'appointment, confirmation, legal, service',
      creator: 'Shahlpo Appointment System',
    });

    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 15; // Global margin for consistency

    let currentY = margin; // Starting Y position for content

    // --- Header ---
    const headerBottomY = 55; // Approximate Y position where header elements end

    // Add logo to the left
    const logoWidth = 50; // Adjust as needed in PDF units (mm)
    const logoHeight = (logoWidth / 400) * 150; // Maintain aspect ratio based on desiredWidth/desiredHeight in useEffect
    const logoX = margin;
    const logoY = margin;
    doc.addImage(logoPngBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);

    // Add address and contact on the right
    doc.setFontSize(9);
    doc.setTextColor(52, 58, 64); // Dark grey
    doc.setFont('helvetica', 'normal');

    const addressLines = [
      'SPC Free Zone, Flexi Desk, Al Zahira Area - Entrance No.2',
      'Ground Floor - Sheikh Mohammed Bin Zayed Rd - Sharjah',
      'United Arab Emirates.',
    ];
    const email = 'info@shahlpo.ae';
    const contact = '+971-585703027';

    let contactDetailsY = logoY; // Start Y position for address relative to logo

    // Calculate maximum width for contact details to align them to the right
    const allContactLines = [
      ...addressLines,
      `Email: ${email}`,
      `Contact: ${contact}`,
    ];
    let maxContactWidth = 0;
    allContactLines.forEach((line) => {
      const width = doc.getTextWidth(line);
      if (width > maxContactWidth) {
        maxContactWidth = width;
      }
    });

    const contactXRightAligned = pageWidth - margin - maxContactWidth;

    addressLines.forEach((line) => {
      doc.text(
        line,
        pageWidth - doc.getTextWidth(line) - margin,
        contactDetailsY
      );
      contactDetailsY += 4; // Line height for address
    });

    contactDetailsY += 2; // Small gap between address and email/contact

    doc.text(
      `Email: ${email}`,
      pageWidth - doc.getTextWidth(`Email: ${email}`) - margin,
      contactDetailsY
    );
    contactDetailsY += 4;

    doc.text(
      `Contact: ${contact}`,
      pageWidth - doc.getTextWidth(`Contact: ${contact}`) - margin,
      contactDetailsY
    );

    // Red line under header
    doc.setDrawColor(219, 34, 23); // Red line matching button color
    doc.setLineWidth(0.5);
    doc.line(margin, headerBottomY, pageWidth - margin, headerBottomY);

    // Add "Appointment Confirmation" centered below the red line
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(52, 58, 64); // Dark grey
    const confirmationText = 'Appointment Confirmation';
    const confirmationTextWidth = doc.getTextWidth(confirmationText);
    doc.text(
      confirmationText,
      (pageWidth - confirmationTextWidth) / 2,
      headerBottomY + 10
    );
    currentY = headerBottomY + 35; // Update currentY for next section

    // --- Appointment Details Section ---
    const detailLabelWidth = 40; // Fixed width for labels
    const detailValueX = margin + detailLabelWidth + 10; // Start X for values
    const lineSpacing = 8; // Consistent line spacing for details

    doc.setFontSize(12);
    doc.setTextColor(33, 37, 41); // Darker text for details

    const addDetail = (label: string, value: string) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, margin + 5, currentY); // Slight indent for label
      doc.setFont('helvetica', 'normal');
      doc.text(value, detailValueX, currentY);
      currentY += lineSpacing;
    };

    // Add a small heading for appointment details
    // doc.setFont('helvetica', 'bold');
    // doc.setFontSize(14);
    // doc.text('Your Appointment Details:', margin, currentY);
    // currentY += 10;

    addDetail('Name', appointmentData.name);
    addDetail('Email', appointmentData.email);
    addDetail('Phone', `+91 ${appointmentData.phone}`);
    addDetail(
      'Service',
      serviceLabels[appointmentData.service] || appointmentData.service
    );
    addDetail(
      'Date & Time',
      `${appointmentData.date} at ${appointmentData.timeSlot}`
    );
    addDetail('Location', appointmentData.location);
    addDetail('Advisor', appointmentData.advisor);
    addDetail('Note', appointmentData.note || 'None');
    addDetail('Total', `AED ${appointmentData.price.toFixed(2)}`); // Format price

    // Add a closing remark
    currentY += 15; // Space before remark
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(108, 117, 125);
    doc.text(
      'Please keep this information handy for your appointment.',
      margin,
      currentY
    );
    currentY += 5;
    doc.text(
      'For any queries, feel free to contact our support at info@shahlpo.ae or +971-585703027.',
      margin,
      currentY
    );

    // --- Footer ---
    const footerY = pageHeight - margin;
    doc.setDrawColor(219, 34, 23); // Red line
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5); // Line above footer
    doc.setFontSize(9);
    doc.setTextColor(108, 117, 125); // Grey for footer text
    doc.setFont('helvetica', 'normal');
    doc.text(
      'Shah Legal Process Outsourcing Fzc.',
      pageWidth / 2, // Center text
      footerY,
      { align: 'center' }
    );

    doc.save(
      `Shahlpo_Appointment_${appointmentData.name.replace(/\s/g, '_')}_${appointmentData.date}.pdf`
    );
    setPdfDownloaded(true); // Mark PDF as downloaded
  };

  const handleCloseModal = () => {
    if (pdfDownloaded) {
      console.log('Closing modal and navigating to home');
      setShowModal(false);
      navigate('/');
    } else {
      Swal.fire({
        title: 'Download Required',
        text: 'Please download your appointment details before proceeding.',
        icon: 'info',
        confirmButtonText: 'OK',
        confirmButtonColor: '#db2217',
      });
    }
  };

  return (
    <div className="p-4 text-center">
      {error && (
        <Alert variant="danger" role="alert">
          {error}
        </Alert>
      )}
      {!showModal && !error && (
        <>
          {/* Optional: You can add a loading spinner or message here while data is being fetched */}
          {/* <h2>Verifying Payment...</h2>
          <p>Please wait while we confirm your appointment.</p> */}
        </>
      )}

      {appointmentData && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          backdrop="static"
          keyboard={false}
          className="appointment-confirmation-modal"
        >
          <Modal.Body>
            <div className="modal-content-wrapper p-3">
              {' '}
              <div className="d-flex justify-content-center align-items-center ">
                <img
                  src={logo}
                  alt="Logo"
                  className="me-3"
                  style={{ width: '200px', height: '50px' }}
                />
              </div>
              <hr />
              <p className="mb-0 text-center text-red fw-semibold lead">
                Appointment Details
              </p>
              <hr />
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Name:</strong>
                </div>
                <div className="col-8 text-start">{appointmentData.name}</div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Email:</strong>
                </div>
                <div className="col-8 text-start">{appointmentData.email}</div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Phone:</strong>
                </div>
                <div className="col-8 text-start">
                  +91 {appointmentData.phone}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Service:</strong>
                </div>
                <div className="col-8 text-start">
                  {serviceLabels[appointmentData.service] ||
                    appointmentData.service}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Date & Time:</strong>
                </div>
                <div className="col-8 text-start">
                  {appointmentData.date} at {appointmentData.timeSlot}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Location:</strong>
                </div>
                <div className="col-8 text-start">
                  {appointmentData.location}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Advisor:</strong>
                </div>
                <div className="col-8 text-start">
                  {appointmentData.advisor}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Note:</strong>
                </div>
                <div className="col-8 text-start">
                  {appointmentData.note || 'None'}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-4 text-start text-muted">
                  <strong>Total:</strong>
                </div>
                <div className="col-8 text-start">
                  AED {appointmentData.price.toFixed(2)}
                </div>
              </div>
              <hr />
              <p className="text-muted small mt-3">
                Please keep this information handy. For any queries, feel free
                to contact our support.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="primary"
              onClick={handleCloseModal}
              disabled={!pdfDownloaded}
              style={{
                borderRadius: '50px',
                backgroundColor: '#db2217',
                border: 'none',
                padding: '10px 20px',
                color: '#fff',
                opacity: pdfDownloaded ? 1 : 0.6,
                cursor: pdfDownloaded ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem',
              }}
            >
              Go to Home
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadPdf}
              style={{
                borderRadius: '50px',
                backgroundColor: '#082341 ',
                border: 'none',
                padding: '10px 20px',
                color: '#fff',
                fontSize: '0.9rem',
              }}
            >
              <i className="bi bi-download me-2"></i> Download PDF
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default Success;
